/**
 * Browser geolocation + backend OSM reverse-geocode helpers.
 * Provider is chosen server-side via GEOCODE_PROVIDER (default: osm).
 */

export const GEOLOCATION_ERROR_MESSAGES = {
  PERMISSION_DENIED:
    "Location permission denied. Allow location access in your browser, or enter your address manually.",
  POSITION_UNAVAILABLE:
    "We couldn't determine your current location. Please try again or enter your address manually.",
  TIMEOUT:
    "Location request timed out. Please try again or enter your address manually.",
  UNSUPPORTED:
    "Your browser does not support location services. Please enter your address manually.",
  UNKNOWN:
    "Unable to get your location. Please enter your address manually.",
};

/**
 * Request the device's current coordinates via the Geolocation API.
 * @returns {Promise<{ latitude: number, longitude: number, accuracy?: number }>}
 */
export function getCurrentCoordinates(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      const error = new Error(GEOLOCATION_ERROR_MESSAGES.UNSUPPORTED);
      error.code = "UNSUPPORTED";
      reject(error);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (geoError) => {
        let message = GEOLOCATION_ERROR_MESSAGES.UNKNOWN;
        let code = "UNKNOWN";

        switch (geoError?.code) {
          case geoError.PERMISSION_DENIED:
          case 1:
            message = GEOLOCATION_ERROR_MESSAGES.PERMISSION_DENIED;
            code = "PERMISSION_DENIED";
            break;
          case geoError.POSITION_UNAVAILABLE:
          case 2:
            message = GEOLOCATION_ERROR_MESSAGES.POSITION_UNAVAILABLE;
            code = "POSITION_UNAVAILABLE";
            break;
          case geoError.TIMEOUT:
          case 3:
            message = GEOLOCATION_ERROR_MESSAGES.TIMEOUT;
            code = "TIMEOUT";
            break;
          default:
            break;
        }

        const error = new Error(message);
        error.code = code;
        error.original = geoError;
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000,
        ...options,
      }
    );
  });
}

/**
 * Call backend reverse-geocode endpoint (OSM Nominatim by default).
 * @param {import('axios').AxiosInstance} api
 * @param {number} latitude
 * @param {number} longitude
 */
export async function reverseGeocodeCoordinates(api, latitude, longitude) {
  const res = await api.get("/geocode/reverse", {
    params: { lat: latitude, lon: longitude },
  });
  return res.data;
}

/**
 * Map reverse-geocode API payload into address form fields.
 * Maps OSM/Nominatim fields to form fields with specific field mapping.
 */
export function mapGeocodeToAddressFields(geocode) {
  if (!geocode || typeof geocode !== "object") {
    return {};
  }

  return {
    // OSM field mapping as requested:
    // house_number -> House / Flat Number
    house_flat_no: geocode.house_number || "",
    // road / neighbourhood / suburb -> Street / Area
    street_area: geocode.street_area || "",
    // city / town / village / municipality -> City
    city: geocode.city || "",
    // county -> District
    district_county: geocode.district || "",
    // state -> State / Province
    state_province: geocode.state || "",
    // country -> Country
    country: geocode.country || "",
    // postcode -> Pincode / Postal Code
    postal_code: geocode.pincode || "",
    // Coordinates
    latitude: geocode.latitude ?? null,
    longitude: geocode.longitude ?? null,
    // Note: display_name is NOT included - removed as requested
  };
}

/**
 * Full flow: browser location → backend reverse geocode → address fields.
 */
export async function autofillAddressFromCurrentLocation(api, options = {}) {
  const coords = await getCurrentCoordinates(options.geoOptions);
  const geocode = await reverseGeocodeCoordinates(
    api,
    coords.latitude,
    coords.longitude
  );
  return {
    coords,
    geocode,
    fields: mapGeocodeToAddressFields(geocode),
  };
}

export function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail || error?.response?.data?.message;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (error?.message) return error.message;
  return fallback || GEOLOCATION_ERROR_MESSAGES.UNKNOWN;
}
